import axios from 'axios';
import { getServerUrl, environment } from '../config';
import ShopifyBulkOperationsService from './shopifyBulkOperationsService';

export class ImageService {
  static async processImage(imageUrl, options = {}) {
    try {
      const response = await axios.post(`${getServerUrl('proxy')}/api/process-image`, {
        imageUrl,
        format: options.format,
        compressionLevel: options.compressionLevel,
        quality: options.quality
      });

      return response.data;
    } catch (error) {
      console.error('Error processing image:', error);
      throw new Error('Failed to process image');
    }
  }

  static formatFileSize(bytes) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  static async getImageFileSize(url) {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const size = response.headers.get('content-length');
      return size ? parseInt(size, 10) : null;
    } catch (error) {
      console.error('Error getting image size:', error);
      return null;
    }
  }

  static calculateSizeReduction(originalSize, newSize) {
    if (!originalSize || !newSize) return 0;
    return Math.round(((originalSize - newSize) / originalSize) * 100);
  }

  static ongoingRequests = new Map();

  static async updateShopifyProductImage(shop, accessToken, imageData) {
    try {
      console.log('Sending update request through proxy:', imageData);
      
      // Extract necessary data
      const { originalImage, processedImage } = imageData;
      
      if (!originalImage || !processedImage) {
        throw new Error('Missing original or processed image data');
      }
      
      // Extract product ID from the original image or from options
      let productId = originalImage.product_id;
      
      // If product_id is not available in originalImage, try to find it in processedImage metadata
      if (!productId && processedImage.metadata?.options?.productId) {
        productId = processedImage.metadata.options.productId;
      }
      
      // Ensure we have a product ID
      if (!productId) {
        throw new Error('Product ID is required for updating images');
      }
      
      // Ensure the processedUrl is a full URL, not a local path
      if (processedImage.processedUrl && processedImage.processedUrl.startsWith('/')) {
        // Convert relative path to full URL using centralized config
        const imageServerUrl = getServerUrl('imageProcessing');
          
        processedImage.processedUrl = `${imageServerUrl}${processedImage.processedUrl}`;
        console.log('Converted processedUrl to full URL:', processedImage.processedUrl);
      }
      
      // Initialize the service
      const bulkService = new ShopifyBulkOperationsService(shop, accessToken);
      
      // Call the updated method with the correct parameters
      const result = await bulkService.updateProductImage(
        productId,
        originalImage,
        processedImage
      );
      
      // Check if this is the last image for this product and trigger batch processing
      // This is determined by checking if the 'isLastImage' flag is set in the imageData
      let finalBatchResult = null;
      if (imageData.isLastImage) {
        console.log('This is the last image for product, triggering batch processing');
        const batchResult = await ShopifyBulkOperationsService.triggerBatchProcessing(
          productId,
          shop,
          accessToken
        );
        if (batchResult) {
          console.log('Batch processing completed successfully');
          finalBatchResult = batchResult;
        } else {
          console.warn('No batch was processed or an error occurred during batch processing');
        }
      }
      
      return {
        success: result.success,
        taskId: result.taskId || `image-update-${Date.now()}`, // Use the taskId from result if available
        newImageId: result.newImage?.id,
        error: result.error,
        batchResult: finalBatchResult
      };
    } catch (error) {
      console.error('Error updating Shopify product image:', error);
      return {
        success: false,
        error: error.message || 'Unknown error'
      };
    }
  }

  static async deleteShopifyProductImage(shop, accessToken, productId, imageId) {
    try {
      // Initialize the service
      const bulkService = new ShopifyBulkOperationsService(shop, accessToken);
      
      // Use the instance method to delete the image
      const result = await bulkService.deleteProductImage(
        productId,
        imageId
      );

      return result;
    } catch (error) {
      console.error('Error deleting Shopify product image:', error);
      throw new Error(`Failed to delete image: ${error.message}`);
    }
  }
};
